import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';

export type TenantMode = 'superadmin' | 'tenantadmin' | 'unknown';

export function useTenantContext() {
  const { role, tenantId, impersonatedTenantId } = useAuthStore();

  const mode = useMemo<TenantMode>(() => {
    // JWT role takes precedence over hostname detection
    // A tenant admin must never see SuperAdmin features, regardless of hostname.
    if (role === 'provideradmin') {
      return 'tenantadmin'; // Provider admins operate within a tenant context
    }

    if (role === 'tenantadmin' || role === 'support') {
      return 'tenantadmin';
    }

    if (role === 'superadmin') {
      return 'superadmin';
    }

    // Fallback: use hostname detection only when role is null/unknown
    const host = window.location.hostname;
    const parts = host.split('.');

    // The configured admin host selects SuperAdmin mode only when the role is unknown.
    if (parts.length > 2 && parts[0] === 'admin') {
      return 'superadmin';
    }

    // localhost or unknown - default to tenantadmin
    return 'tenantadmin';
  }, [role]);

  const isSuperAdmin = mode === 'superadmin';
  const isTenantAdmin = mode === 'tenantadmin';
  const activeTenantId = impersonatedTenantId ?? tenantId;
  // A SuperAdmin scoped to a tenant via the header switcher / "View as Tenant".
  // They keep their SuperAdmin identity (so the switcher + exit banner stay
  // visible), but the rest of the UI should behave like that tenant's admin.
  const isImpersonating = isSuperAdmin && impersonatedTenantId != null;

  return {
    mode,
    isSuperAdmin,
    isTenantAdmin,
    isImpersonating,
    tenantId,
    activeTenantId,
    impersonatedTenantId,
  };
}
